import { useEffect } from 'react';
import { useTaxForm } from './TaxFormContext';
import { calculateLiveTax } from '@/utils/taxEngine';

const safeFloat = (v: any) => { const n = parseFloat(String(v || 0)); return Number.isFinite(n) ? n : 0; };
const toArray = (val: any) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

export const useLiveTaxEngine = () => {
  const { formData, updateFormData, isLoading } = useTaxForm();

  const bankDetails = toArray(formData.bank_details);
  const motorVehicles = toArray(formData.motor_vehicles);
  const properties = toArray(formData.properties);
  const investments = toArray(formData.investments);
  const preciousPossessions = toArray(formData.precious_possessions);
  const liabilitiesArray = toArray(formData.liabilities_details);

  const taxableIncome = safeFloat(formData.income_salary) + safeFloat(formData.income_business) + safeFloat(formData.income_property) + safeFloat(formData.income_profit_debt);
  const totalGrossIncome = taxableIncome + safeFloat(formData.income_pension) + safeFloat(formData.income_agricultural) + safeFloat(formData.income_foreign_source);
  
  const whtDeductions = safeFloat(formData.wht_mobile_internet) + safeFloat(formData.wht_vehicle_token) + safeFloat(formData.wht_other) + safeFloat(formData.tax_deducted_salary) + safeFloat(formData.tax_deducted_business) + safeFloat(formData.tax_deducted_property) + safeFloat(formData.tax_deducted_profit_debt);
  
  const { taxAmount, classApplied } = calculateLiveTax(taxableIncome, safeFloat(formData.income_salary), formData.tax_year || "2024");
  const netTaxLiability = Math.max(0, taxAmount - whtDeductions);

  const personalExpenses = safeFloat(formData.personal_expenses);
  const totalLiabilities = liabilitiesArray.reduce((sum, l) => sum + safeFloat(l.amount), 0);
  
  const grossAssets = safeFloat(formData.cash_in_hand) + 
     bankDetails.reduce((sum, b) => sum + safeFloat(b.june_balance), 0) +
     properties.reduce((sum, p) => sum + safeFloat(p.cost), 0) +
     motorVehicles.reduce((sum, v) => sum + safeFloat(v.cost), 0) +
     investments.reduce((sum, i) => sum + safeFloat(i.closing_balance), 0) +
     preciousPossessions.reduce((sum, p) => sum + safeFloat(p.estimated_value), 0);
     
  const currentNetAssets = grossAssets - totalLiabilities;
  const previousAssets = safeFloat(formData.previous_year_net_assets);
  const assetIncrease = currentNetAssets - previousAssets;
  
  const isReconciled = Math.abs((totalGrossIncome - personalExpenses) - assetIncrease) < 10;
  const variance = Math.abs((totalGrossIncome - personalExpenses) - assetIncrease);

  useEffect(() => {
    if (isLoading) return; 
    // Silently sync total computed values
    updateFormData({
       total_income: taxableIncome,
       calculated_tax: taxAmount,
       tax_class: classApplied,
       current_year_net_assets: currentNetAssets,
       wealth_increase: assetIncrease,
       reconciled_amount: totalGrossIncome - personalExpenses
    }, true);
  }, [totalGrossIncome, taxAmount, classApplied, currentNetAssets, assetIncrease, personalExpenses, isLoading]);

  return {
    taxableIncome,
    totalGrossIncome,
    whtDeductions,
    taxAmount,
    classApplied,
    netTaxLiability,
    personalExpenses,
    totalLiabilities,
    grossAssets,
    currentNetAssets,
    previousAssets,
    assetIncrease,
    isReconciled,
    variance
  };
};
